import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { FileText } from 'lucide-react';
import { api } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDate } from '@/lib/utils';

interface Rmp {
  id: string;
  product: string | null;
  activeSubstance: string | null;
  version: string | null;
  status: string | null;
  updatedAt: string;
}

export function RmpListPage() {
  const rmps = useQuery({
    queryKey: ['rmps'],
    queryFn: async () => {
      try {
        return (await api.get('/api/rmp?pageSize=100')).data;
      } catch {
        return { items: [], total: 0 };
      }
    },
  });

  const items: Rmp[] = rmps.data?.items ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Risk management plans</h1>
          <p className="text-sm text-muted-foreground">
            EU GVP Module V / FDA REMS structure. Parts I-VII with e-signed approval.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>RMPs</CardTitle>
          <CardDescription>Latest version per product shown.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Product</TableHead>
                <TableHead>Active substance</TableHead>
                <TableHead>Version</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Last updated</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rmps.isLoading && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-sm text-muted-foreground">
                    Loading...
                  </TableCell>
                </TableRow>
              )}
              {!rmps.isLoading && items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="p-10 text-center text-sm text-muted-foreground">
                    <FileText className="mx-auto mb-2 h-6 w-6" /> No RMPs yet.
                  </TableCell>
                </TableRow>
              )}
              {items.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-medium">{r.product ?? '-'}</TableCell>
                  <TableCell className="text-sm">{r.activeSubstance ?? '-'}</TableCell>
                  <TableCell className="font-mono text-xs">{r.version ?? '-'}</TableCell>
                  <TableCell>
                    <StatusBadge s={r.status} />
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {formatDate(r.updatedAt)}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button asChild variant="outline" size="sm">
                      <Link to={`/rmp/${r.id}`}>Open</Link>
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function StatusBadge({ s }: { s: string | null }) {
  if (!s) return <span className="text-muted-foreground">-</span>;
  const v: Record<string, 'secondary' | 'info' | 'success' | 'warning' | 'destructive'> = {
    DRAFT: 'secondary',
    IN_REVIEW: 'warning',
    SUBMITTED: 'info',
    APPROVED: 'success',
    RETIRED: 'secondary',
  };
  return <Badge variant={v[s] ?? 'secondary'}>{s.replace(/_/g, ' ')}</Badge>;
}
